import * as Yup from 'yup';
import { useFieldArray } from 'react-hook-form';
import { Button, Card, CardContent, Dialog, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { DeleteButton } from '../../../components/delete-button.component';
import { Icons } from '../../../assets/icons';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { stateApiKeyStore } from '../../../api/states-api-key-store';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { municipalityApiKeyStore } from '../../../api/masters/municipality-api-key-store';
import { FormFormattedInput } from '../../../components/hook-form/form-formatted-input.component';
import { ZIPCODE_FORMAT } from '../../../constants';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { countyApiKeyStore } from '../../../api/county-api-key-store';
import { CreateScatteredUnitButton } from './create-scattered-unit-button';
import { useEffect, useRef, useState } from 'react';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { ApiErrorComponent } from '../../../components/api-error.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useAuthorize } from '../../../hooks/use-authorize';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';

interface BuildingUnitsListFormProps {
  projectID: number;
  singleOrMultiLots: any[];
  isTimeAndMaterial: boolean;
  page?: string;
  syncWithDux: any;
}

const lotValidationSchema = Yup.object().shape({
  unitName: Yup.string().required('Lot/Unit is required').max(9, 'Lot/Unit cannot exceed 9 characters'),
  address: Yup.string().required('Address is required').max(31, 'Address cannot exceed 31 characters'),
  // jobNumber: Yup.string().required('Builder Job Number is required'),
  jobsite: Yup.string().required('Jobsite is required'),
  //   city: Yup.string().required('City is required'),
  //   stateID: Yup.object()
  //     .shape({
  //       stateID: Yup.number().required('State is required'),
  //     })
  //     .nullable()
  //     .required('State is required'),
  //   zipcode: Yup.string()
  //     .required('Zip Code is required')
  //     .matches(/^\d{5}$/, 'Zip Code must be exactly 5 digits'),
  //   countyID: Yup.number().required('County is required'),
  //   municipalityID: Yup.object()
  //     .shape({
  //       municipalityID: Yup.number().required('Municipality is required'),
  //     })
  //     .nullable()
  //     .required('Municipality is required'),
});

const timeAndMaterialValidationSchema = Yup.object().shape({
  singleOrMultiLots: Yup.array().of(lotValidationSchema),
});
// .test('unique-unitName', 'Each unit name must be unique', function (value: any) {
//   const allUnitNames = value.singleOrMultiLots.map((lot: any) => lot.unitName);
//   const uniqueUnitNames = new Set(allUnitNames);
//   return uniqueUnitNames.size === allUnitNames.length;
// })
// .test('unique-address', 'Each address must be unique', function (value: any) {
//   const allAddresses = value.singleOrMultiLots.map((lot: any) => lot.address);
//   const uniqueAddresses = new Set(allAddresses);
//   return uniqueAddresses.size === allAddresses.length;
// })
// .test('unique-jobsite', 'Each jobsite must be unique', function (value: any) {
//   const allJobsites = value.singleOrMultiLots.map((lot: any) => lot.jobsite);
//   const uniqueJobsites = new Set(allJobsites);
//   return uniqueJobsites.size === allJobsites.length;
// })
// .test('unique-jobNumber', 'Each builder job Number must be unique', function (value: any) {
//   const allJobNumbers = value.singleOrMultiLots
//     .map((lot: any) => lot.jobNumber)
//     .filter((jobNumber: any) => !!jobNumber);
//   const uniqueJobNumbers = new Set(allJobNumbers);
//   return uniqueJobNumbers.size === allJobNumbers.length;
// });
// .test('unique-addresses', 'Address must be unique', function (value: any) {
//   const allAddresses = value.singleOrMultiLots.flatMap((lot: any) => lot.address);
//   const uniqueAddresses = new Set(allAddresses);

//   return uniqueAddresses.size === allAddresses.length;
// });

export const SactteredLoTEditForm = ({
  projectID,
  isTimeAndMaterial,
  singleOrMultiLots,
  page,
  syncWithDux
}: BuildingUnitsListFormProps) => {
  const form = useReactHookForm({
    defaultValues: {
      projectID,
      singleOrMultiLots,
    },
    yupSchema: timeAndMaterialValidationSchema,
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'singleOrMultiLots',
    keyName: 'tID',
  });
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isLoading, mutateAsync } = useSaveData(projectsApiKeyStore.updateLotInfo());
  const [openDialog, setOpenDialog] = useState(false);
  const [transformedData, setTransformedData] = useState<any>({});
  const watchFields = form.watch();
  const initialJobsites = useRef<string[]>(fields?.map((field: any) => field.jobsite || ''));
  const { data } = useLoadData<ApiResponse<any>>(proctorResultApiKeyStore.getProjectProctorResults(projectID));

  const handleLoadCreated = (lot: any) => {
    const transformedLot = {
      ...lot,
      isUnitCreationCompleted: true,
      stateID: !lot?.stateID ? undefined : { stateID: lot?.stateID || 0 },
      municipalityID: !lot?.municipalityID ? undefined : { municipalityID: lot?.municipalityID || 0 },
    };

    append(transformedLot);
    initialJobsites.current.push(lot.jobsite || '');
  };

  const handleRemove = (index: number) => {
    remove(index);
    initialJobsites.current.splice(index, 1);
  };

  const handleSubmit = async (values: any) => {
    const transformedValues = {
      ...values,
      singleOrMultiLots: values.singleOrMultiLots.map((lot: any) => ({
        ...lot,
        stateID: lot.stateID?.stateID ?? 0,
        municipalityID: lot.municipalityID?.municipalityID ?? 0,
        squareFeet: !lot?.squareFeet ? null : Number(lot?.squareFeet),
        proctorResult: !lot?.proctorResult
          ? null
          : {
            proctorResultsID: lot.proctorResult.proctorResultsID,
            unitID: lot.unitID,
            proctorFileName: lot.proctorResult.proctorFileName,
          },

        // proctorResult: lot.proctorResult
        //   ? lot.proctorResult.map((proctor: any) => ({
        //       proctorResultsID: proctor.proctorResultsID,
        //       unitID: lot.unitID,
        //     }))
        //   : null,
      })),
    };

    const { type, ...dataWithoutType } = transformedValues;
    const hasCreateWOTrue = transformedValues?.singleOrMultiLots?.some((item: any) => item?.createWO === true);
    if (transformedValues?.singleOrMultiLots?.length > 0 && !hasCreateWOTrue && type == 'with-workorder') {
      showSnackbar(`Please select at least one 'Create Work Order' before saving.`, 'error');
      return;
    }
    if (hasCreateWOTrue && type == 'without-workorder') {
      showSnackbar(`Please do not select 'Create Work Order' before saving.`, 'error');
      return;
    }
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: type == 'with-workorder' } });
    // setOpenDialog(true);
    // setTransformedData(transformedValues);

    //  await mutateAsync({ data: transformedValues });
  };

  // const closeDialog = () => {
  //   setIsOpen(false);
  // };

  // const submitHanlder = async () => {
  //   await mutateAsync({ data: transformedData });
  //   setIsOpen(false);
  // };

  const handleUnitNameChange = (index: number, value: string) => {
    const initialJobsite = initialJobsites?.current[index];

    const jobsite = form.getValues(`singleOrMultiLots.${index}.jobsite`);
    const newJobsite = `${initialJobsite}${value}`?.trim();
    form.setValue(`singleOrMultiLots.${index}.jobsite`, newJobsite);

    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
      return;
    }

    // ✅ Check duplicates within form
    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u.unitName?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.unitName`, {
        type: "manual",
        message: "Lot/Unit address already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
    }
  };

  const handleJobNumberChange = (index: number, value: string) => {

    // ✅ If empty, just clear error & return
    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
      return;
    }

    // ✅ Check duplicates within form
    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u?.jobNumber?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobNumber`, {
        type: "manual",
        message: "Job number already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
    }
  };

  const handleAddressChange = (index: number, value: string) => {

    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
      return;
    }

    // ✅ Check duplicates within form
    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u?.address?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.address`, {
        type: "manual",
        message: "Address already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
    }
  };

  const handleJobnameNameChange = (index: number, value: any) => {
    initialJobsites.current[index] = value;

    if (!value || value?.trim() === "") {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
      return;
    }

    const allUnits = form.getValues("singleOrMultiLots")?.map((u: any) => u?.jobsite?.trim()?.toLowerCase());
    const isDuplicate = allUnits?.filter((u: string) => u?.toLowerCase()?.trim() === value?.trim()?.toLowerCase())?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobsite`, {
        type: "manual",
        message: "Jobsite already exists.",
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
    }
  };

  const closeDialog = async () => {
    const { type, ...dataWithoutType } = transformedData;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: false } });
    setOpenDialog(false);
  };

  const handleSubmitConfirm = async () => {
    const { type, ...dataWithoutType } = transformedData;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: true } });

    setOpenDialog(false);
  };

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const onTypedSubmit = (type: string) =>
    form.handleSubmit(
      (values) => handleSubmit({ ...values, type }),
      handleError
    )();

  const disabledBuildingPermit = (index: number) => {
    return form.getValues('singleOrMultiLots')[index]?.projectSyncWithDUX;
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      <ReactHookForm
        formName="TIME_AND_MATERIAL_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isLoading}
      >
        <Card elevation={0}>
          <CardContent style={{ padding: '25px', paddingTop: '30px' }}>
            <Grid container spacing={2}>
              {fields.map((field, index) => (
                <Grid key={field.tID} item lg={12} sm={12} xl={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={8} sm={8} xl={8} xs={8}>
                      <div style={{ display: 'none' }}>
                        <label>{form.getValues(`singleOrMultiLots.${index}.unitNumber`)}</label>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            // disabled={true}
                            name={`singleOrMultiLots.${index}.jobsite`}
                            label={isTimeAndMaterial ? 'Inspection Title' : 'Jobsite'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                value
                              )
                            }
                            onChange={(e: any) => handleJobnameNameChange(index, e)}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.unitName`}
                            label={'Lot/Unit'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                value,
                                ''
                              )
                            }
                            onChange={(e: any) => handleUnitNameChange(index, e)}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.jobNumber`}
                            label={'Builder Job Number'}
                            isCustomError={true}
                            onChange={(e: any) => handleJobNumberChange(index, e)}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobNumber(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                '',
                                value
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput
                            name={`singleOrMultiLots.${index}.address`}
                            label="Address"
                            onChange={(e: any) => handleAddressChange(index, e)}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                                projectID,
                                value,
                                form.getValues(`singleOrMultiLots.${index}.unitID`)
                              )
                            }
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput name={`singleOrMultiLots.${index}.city`} label="City" />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.stateID`}
                            label="State"
                            getQueryKeyConfig={() => stateApiKeyStore.getStates()}
                            optionsValueKey="stateID"
                            optionsLabelKey="stateName"
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormFormattedInput
                            name={`singleOrMultiLots.${index}.zipcode`}
                            label="Zip Code"
                            format={ZIPCODE_FORMAT}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormSelectDynamic
                            name={`singleOrMultiLots.${index}.countyID`}
                            label="County"
                            getQueryKeyConfig={() => countyApiKeyStore.getCounties()}
                            optionsValueKey="countyID"
                            optionsLabelKeys={['countyName']}
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.municipalityID`}
                            label="Municipality"
                            disabled={!watchFields?.singleOrMultiLots[index]?.stateID}
                            getQueryKeyConfig={() =>
                              municipalityApiKeyStore.getMunicipalityByStateID(
                                watchFields?.singleOrMultiLots[index]?.stateID?.stateID
                              )
                            }
                            optionsValueKey="municipalityID"
                            optionsLabelKey="muncipalityName"
                          />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormInput name={`singleOrMultiLots.${index}.squareFeet`} label={'Square Feet'} />
                        </Grid>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.proctorResult`}
                            label="Proctor Result"
                            // getQueryKeyConfig={() => proctorResultApiKeyStore.getProjectProctorResults(projectID)}
                            optionsValueKey="proctorResultsID"
                            optionsLabelKey="proctorFileName"
                            dataItems={!data ? [] : data?.data}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={4} sm={4} xl={4} xs={4}>
                      <Grid
                        container
                        direction="row"
                        sx={{
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                        }}
                      >
                        <FormCheckbox label="Create WO" name={`singleOrMultiLots.${index}.createWO`} />
                        <FormCheckbox label="Sync With DUX" name={`singleOrMultiLots.${index}.syncWithDUX`} disabled={!disabledBuildingPermit(index)} />
                        <FormInput label="Building Permit#" name={`singleOrMultiLots.${index}.buildingPermit`} styleField={{ width: '200px' }} />
                        {page && form.getValues(`singleOrMultiLots.${index}.isUnitCreationCompleted`) && (
                          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                            <IconButton
                              style={{ padding: '10px 2px 5px 15px' }}
                              aria-label="view"
                              onClick={() =>
                                navigate(`/units/profile/${form.getValues(`singleOrMultiLots.${index}.unitID`)}`)
                              }
                            >
                              <Tooltip title={<Typography variant="body1">View</Typography>}>
                                <VisibilityIcon className="btnView" />
                              </Tooltip>
                            </IconButton>
                          </GuardedComponent>
                        )}
                        <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                          {!form.getValues(`singleOrMultiLots.${index}.disableDelete`) ? (
                            <DeleteButton
                              data={undefined}
                              deleteMutationConfig={() =>
                                projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                              }
                              onDeleted={() => handleRemove(index)}
                            >
                              <>
                                <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                  <img src={Icons.Delete} alt="DELETE" />
                                </Tooltip>
                              </>
                            </DeleteButton>
                          ) : (
                            <DeleteButton
                              data={undefined}
                              deleteMutationConfig={() =>
                                projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                              }
                              onDeleted={() => handleRemove(index)}
                              isDisabled={true}
                            >
                              <>
                                <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                  <img src={Icons.DisabledDelete} alt="DELETE" />
                                </Tooltip>
                              </>
                            </DeleteButton>
                          )}
                        </GuardedComponent>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </CardContent>
          <Grid item lg={1} sm={1} xl={1} xs={1}>
            <CreateScatteredUnitButton
              projectID={projectID}
              onUnitCreated={handleLoadCreated}
              floor={{ floorID: undefined }}
              syncWithDux={syncWithDux}
            />
          </Grid>
          {openDialog && (
            <Dialog
              open={openDialog}
              className="modalpopup"
              title={'CONFIRM STATUS'}
            // onClose={closeDialog}
            >
              <div>
                <div style={{ padding: '25px 50px', textAlign: 'center' }}>
                  <div>Are you sure you want to generate work orders for all the newly added Lot/Units?</div>
                  {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
                </div>
              </div>
              <DialogActionsBar>
                {isReadOnlyPermission && (
                  <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setOpenDialog(false)}>
                    Cancel
                  </Button>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
                  <button
                    className="btn btn-sm btn-primary btn-lightGrey"
                    style={{
                      backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                      color: isReadOnlyPermission ? 'black' : '#fff',
                      cursor: 'pointer', padding: 2, marginRight: 6
                    }}
                    onClick={closeDialog}
                    autoFocus
                    disabled={isReadOnlyPermission}
                  >
                    No
                  </button>

                  <button
                    className="btn btn-sm btn-primary  btn-delete"
                    style={{
                      backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                      color: isReadOnlyPermission ? 'black' : '#fff',
                      cursor: 'pointer', padding: 2
                    }}
                    type="button"
                    onClick={handleSubmitConfirm}
                    disabled={isLoading || isReadOnlyPermission}
                  >
                    Yes
                  </button>
                </div>
              </DialogActionsBar>
            </Dialog>
          )}
          {/* {isOpen && (
            <Dialog className="modalpopup" title={'CONFIRM SAVE'} onClose={closeDialog}>
              <div>
                <div style={{ padding: '25px 50px' }}>
                  <div>Are you sure you want to continue?</div>
                  <LoadingIndicator isLoading={isLoading} />
               
                </div>
              </div>{' '}
              <DialogActionsBar>
                <div className=" spaceBetween">
                  <button
                    style={{ cursor: 'pointer' }}
                    className="btn btn-sm btn-default btn-lightGrey"
                    onClick={closeDialog}
                    autoFocus
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary btn-delete"
                    type="button"
                    onClick={submitHanlder}
                    disabled={isLoading}
                    style={{ cursor: 'pointer' }}
                  >
                    Yes
                  </button>
                </div>
              </DialogActionsBar>
            </Dialog>
          )} */}
        </Card>
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
          <Grid
            item
            lg={12}
            sm={12}
            xl={12}
            xs={12}
            style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button
              type="button"
              className="btn btn-primary btnIcon"
              variant="text"
              style={{ backgroundColor: '#e67e22' }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('without-workorder')}>
              Create Lots/Units without workorders
            </Button>
            <Button
              type="button"
              className="btn btn-primary btnIcon"
              variant="text"
              style={{ backgroundColor: '#239b56' }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('with-workorder')}>
              Create Lots/Units with workorders
            </Button>
          </Grid>
        </GuardedComponent>
      </ReactHookForm>
    </div>
  );
};
